import { useCallback } from 'react';
import { ScrollView } from 'react-native';
import { Colors, Flex, IconClose, RemoteImage, Typography } from '@banhala/ui-kit';
import type {
  CheckboxFilterItemWithLogging,
  CheckboxFilterRow as CheckboxFilterRowType,
} from './types';
import type { IScreensLogging } from '../../../../api/types/IScreenLogging';

type CheckboxFilterRowProps = {
  row: CheckboxFilterRowType;
  onToggleItem: (
    queryName: string,
    queryValue: string,
    itemText: string,
    isSelected: boolean,
    itemLogging?: IScreensLogging,
  ) => void;
};

export const CheckboxFilterRow = ({
  row,
  onToggleItem,
}: CheckboxFilterRowProps) => {
  const handlePressItem = useCallback(
    (itemWithLogging: CheckboxFilterItemWithLogging) => {
      onToggleItem(
        row.query_name,
        itemWithLogging.item.query_value,
        itemWithLogging.item.text,
        itemWithLogging.item.is_selected,
        itemWithLogging.logging,
      );
    },
    [onToggleItem, row.query_name],
  );

  const hasTitle = row.title != null && row.title.length > 0;

  return (
    <Flex.Row alignItems="center" height={48}>
      {hasTitle && (
        <Flex width={64} margin={{ left: 16 }}>
          <Typography
            type="subtitle2"
            color="content_secondary"
            numberOfLines={1}
          >
            {row.title}
          </Typography>
        </Flex>
      )}
      <ScrollView
        horizontal
        showsHorizontalScrollIndicator={false}
        alwaysBounceHorizontal
        contentContainerStyle={{
          flexDirection: 'row',
          alignItems: 'center',
          gap: 4,
          paddingLeft: hasTitle ? 0 : 12,
          paddingRight: 12,
        }}
      >
        {row.filter_item_list.map(itemWithLogging => (
          <CheckboxFilterChip
            key={itemWithLogging.item.query_value}
            itemWithLogging={itemWithLogging}
            onPress={handlePressItem}
          />
        ))}
      </ScrollView>
    </Flex.Row>
  );
};

const CheckboxFilterChip = ({
  itemWithLogging,
  onPress,
}: {
  itemWithLogging: CheckboxFilterItemWithLogging;
  onPress: (itemWithLogging: CheckboxFilterItemWithLogging) => void;
}) => {
  const { item } = itemWithLogging;
  const handlePress = useCallback(() => {
    onPress(itemWithLogging);
  }, [onPress, itemWithLogging]);

  return (
    <Flex.Row
      alignItems="center"
      onPress={handlePress}
      padding={{ horizontal: 6, vertical: 6 }}
      borderRadius={16}
      backgroundColor={item.is_selected ? 'blue5' : undefined}
      gap={4}
    >
      {item.image != null && item.image.length > 0 && (
        <Flex
          overflow="hidden"
          width={20}
          height={20}
          borderRadius={10}
          borderWidth={1}
          borderColorValue="#0000001f"
        >
          <RemoteImage
            uri={item.image}
            width={20}
            height={20}
            resizeMode="cover"
          />
        </Flex>
      )}
      <Typography
        type={item.is_selected ? 'subtitle3' : 'body3'}
        color="content_slate_primary"
      >
        {item.text}
      </Typography>
      {item.is_selected && (
        <IconClose size={16} fill={Colors.content_slate_primary} />
      )}
    </Flex.Row>
  );
};
